import { z } from "zod";

export const INFERENCE = {
  age: ["18-24", "25-34", "35-44", "45-54", "55-64"],
  occupation: [
    "Student",
    "Self-employed",
    "Freelancer",
    "Entry-level professional",
    "Mid-level professional",
    "Senior professional",
    "Executive",
    "Unemployed",
    "Retired",
    "Homemaker",
  ],
  income: [
    "Under $20,000",
    "$20,000 - $39,999",
    "$40,000 - $59,999",
    "$60,000 - $79,999",
    "$80,000 - $99,999",
    "$100,000 - $149,999",
    "$150,000+",
  ],
  gender: ["Male", "Female", "Non-binary", "Gender fluid", "Prefer not to say"],
};

type faqType = {
  questionType: "product" | "general";
  type: "text" | "product-list";
  question: string;
  answer: string;
  metadata: {
    targetAge: typeof INFERENCE.age;
    targetOccupation: typeof INFERENCE.occupation;
    targetIncome: typeof INFERENCE.income;
    targetGender: typeof INFERENCE.gender;
    complexity: "Basic" | "Intermediate" | "Advanced";
  };
};

export function filterFAQs(faqs: faqType[], inference: any) {
  const userInference = parse(inference);
  return faqs
    .map((faq) => ({
      ...faq,
      relevanceScore: calculateRelevance(faq, userInference),
    }))
    .filter((faq) => faq.relevanceScore > 0)
    .sort((a, b) => b.relevanceScore - a.relevanceScore)
    .slice(0, 5);
}

function calculateRelevance(faq: faqType, userInference: any) {
  let score = 0;
  const { metadata } = faq;

  if (isTargetMatch(metadata.targetAge, userInference.age)) score += 1;
  if (isTargetMatch(metadata.targetOccupation, userInference.occupation))
    score += 2;
  if (isTargetMatch(metadata.targetIncome, userInference.income)) score += 1;
  if (isTargetMatch(metadata.targetGender, userInference.gender)) score += 1;
  score += getComplexityScore(metadata.complexity, userInference.occupation);

  return score;
}

function isTargetMatch(
  targetArray: Partial<typeof INFERENCE>[keyof typeof INFERENCE],
  userValue: string,
) {
  return targetArray?.includes(userValue);
}

function parse(inferece: any) {
  return {
    age: inferece?.demographic?.age,
    occupation: inferece?.demographic?.occupation,
    income: inferece?.demographic?.income,
    gender: inferece?.demographic?.gender,
  };
}

function getComplexityScore(
  complexity: "Basic" | "Intermediate" | "Advanced",
  occupation: string,
): number {
  const complexityMap: Record<
    string,
    ("Basic" | "Intermediate" | "Advanced")[]
  > = {
    Executive: ["Basic", "Intermediate", "Advanced"],
    "Senior professional": ["Basic", "Intermediate", "Advanced"],
    "Mid-level professional": ["Basic", "Intermediate", "Advanced"],
    "Entry-level professional": ["Basic", "Intermediate"],
    "Self-employed": ["Basic", "Intermediate"],
    Freelancer: ["Basic", "Intermediate"],
    Student: ["Basic"],
    Unemployed: ["Basic"],
    Retired: ["Basic"],
    Homemaker: ["Basic"],
  };

  const allowedComplexities = complexityMap[occupation] || ["Basic"];

  return allowedComplexities.includes(complexity) ? 1 : -1;
}
